import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AnalysisDataProvider } from '../../contexts/AnalysisDataContext';
import { AnalysisCategory } from '../../services/subscriptionService';
import { useAnalysisEntitlement } from '../../hooks/analysis/useAnalysisEntitlement';
import SubscriptionGate from './SubscriptionGate';
import AnalysisLayout from './AnalysisLayout';
import AnalysisDashboard from './AnalysisDashboard';
import SalesAnalysis from './views/SalesAnalysis';
import OccupancyAnalysis from './views/OccupancyAnalysis';
import PaymentAnalysis from './views/PaymentAnalysis';
import AnomalyAnalysis from './views/AnomalyAnalysis';
import ProductAnalysis from './views/ProductAnalysis';
import CancellationAnalysis from './views/CancellationAnalysis';
import MemberAnalysis from './views/MemberAnalysis';
import PacingAnalysis from './views/PacingAnalysis';

/** Deep links to categories outside the subscription redirect to the hub. */
const CategoryGuard: React.FC<{ category: AnalysisCategory; children: React.ReactElement }> = ({
  category,
  children,
}) => {
  const { entitlement } = useAnalysisEntitlement();
  if (!entitlement.enabled_categories.includes(category)) {
    return <Navigate to="/analysis" replace />;
  }
  return children;
};

/**
 * /analysis/* subtree. The AnalysisDataProvider is mounted here — not in
 * the global provider stack — so a season of reservations is only held in
 * memory while the manager is in the Analysis section.
 */
const AnalysisRoutes: React.FC = () => (
  <SubscriptionGate>
    <AnalysisDataProvider>
      <AnalysisLayout>
        <Routes>
          <Route path="/" element={<AnalysisDashboard />} />
          <Route
            path="/sales"
            element={<CategoryGuard category="sales"><SalesAnalysis /></CategoryGuard>}
          />
          <Route
            path="/occupancy"
            element={<CategoryGuard category="occupancy"><OccupancyAnalysis /></CategoryGuard>}
          />
          <Route
            path="/payments"
            element={<CategoryGuard category="payments"><PaymentAnalysis /></CategoryGuard>}
          />
          <Route
            path="/anomalies"
            element={<CategoryGuard category="anomalies"><AnomalyAnalysis /></CategoryGuard>}
          />
          <Route
            path="/product"
            element={<CategoryGuard category="product"><ProductAnalysis /></CategoryGuard>}
          />
          <Route
            path="/cancellations"
            element={<CategoryGuard category="cancellations"><CancellationAnalysis /></CategoryGuard>}
          />
          <Route
            path="/members"
            element={<CategoryGuard category="members"><MemberAnalysis /></CategoryGuard>}
          />
          <Route
            path="/pacing"
            element={<CategoryGuard category="pacing"><PacingAnalysis /></CategoryGuard>}
          />
          <Route path="*" element={<Navigate to="/analysis" replace />} />
        </Routes>
      </AnalysisLayout>
    </AnalysisDataProvider>
  </SubscriptionGate>
);

export default AnalysisRoutes;
